import React, { useState, useEffect, useMemo } from 'react';
import { api } from '@/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  BarChart3, AlertTriangle, CheckCircle2, Clock, FileText, 
  Download, Filter, Loader2, Lock, Scale
} from 'lucide-react';

const BreakdownBar = ({ data, colorMap }) => {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return <p className="text-xs text-slate-400">No data</p>;
  
  const sorted = Object.entries(data).sort((a, b) => b[1] - a[1]);
  
  return (
    <div className="space-y-1.5">
      {sorted.map(([key, count]) => {
        const pct = Math.round((count / total) * 100);
        const color = colorMap?.[key] || 'bg-slate-200';
        return (
          <div key={key} className="flex items-center gap-2">
            <span className="text-xs text-slate-600 w-40 truncate flex-shrink-0" title={key}>
              {key.replace(/_/g, ' ')}
            </span>
            <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
              <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-mono text-slate-700 w-12 text-right flex-shrink-0">{count}</span>
            <span className="text-xs text-slate-400 w-10 text-right flex-shrink-0">{pct}%</span>
          </div>
        );
      })}
    </div>
  );
};

const sourceColors = {
  manual: 'bg-slate-400',
  irs: 'bg-blue-500',
  rfi: 'bg-indigo-500',
  drawing_issue: 'bg-purple-500',
  shopdrawing_tracker: 'bg-violet-500',
  site_diary: 'bg-teal-500',
  email_import: 'bg-cyan-500',
  ai_extraction: 'bg-amber-500',
  other_linked_module: 'bg-slate-500',
};

const classificationColors = {
  not_required: 'bg-slate-300',
  pending: 'bg-amber-500',
  completed: 'bg-green-500',
};

const statusColors = {
  draft: 'bg-slate-400',
  pending_classification: 'bg-amber-400',
  clause_assigned: 'bg-cyan-500',
  ready_for_notice: 'bg-teal-500',
  notice_created: 'bg-blue-500',
  notice_issued: 'bg-indigo-500',
  claim_created: 'bg-violet-500',
  claim_submitted: 'bg-purple-500',
};

export default function EventAnalyticsTab({ projectId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterFamily, setFilterFamily] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterClassification, setFilterClassification] = useState('all');

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.events.getReport(projectId)
      .then(res => setReport(res.data))
      .catch(err => toast.error('Failed to load event report'))
      .finally(() => setLoading(false));
  }, [projectId]);

  const filteredEvents = useMemo(() => {
    if (!report?.events) return [];
    return report.events.filter(e => {
      if (filterFamily !== 'all' && e.event_family !== filterFamily) return false;
      if (filterSource !== 'all' && e.event_source !== filterSource) return false;
      if (filterClassification !== 'all' && e.classification_status !== filterClassification) return false;
      return true;
    });
  }, [report, filterFamily, filterSource, filterClassification]);

  const handleExportCSV = () => {
    if (!filteredEvents.length) return;
    const headers = ['Event #', 'Family', 'Sub-Type', 'Source', 'Status', 'Classification', 'Responsible Party', 'Start Date', 'Days Ongoing', 'Clause', 'Contract', 'Entitlement', 'Has Notice', 'Has Claim'];
    const rows = filteredEvents.map(e => [
      e.event_number, e.event_family, e.event_sub_type || '', e.event_source,
      e.status, e.classification_status, e.responsible_party, e.start_date?.split('T')[0] || '',
      e.days_ongoing, e.clause_reference || '', e.contract_type || '',
      e.entitlement_category || '', e.has_notice ? 'Yes' : 'No', e.has_claim ? 'Yes' : 'No'
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${(v||'').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event_analytics_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!report || report.total === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-slate-500">
          <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          <p>No delay events found for this project.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="event-analytics-panel">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-800">{report.total}</p>
            <p className="text-xs text-slate-500 uppercase font-medium">Total Events</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{report.by_classification_status?.pending || 0}</p>
            <p className="text-xs text-amber-600 uppercase font-medium">Unclassified</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{report.notice_readiness?.ready || 0}</p>
            <p className="text-xs text-green-600 uppercase font-medium">Notice Ready</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{report.notice_readiness?.not_ready || 0}</p>
            <p className="text-xs text-red-600 uppercase font-medium">Notice Blocked</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{report.by_cease_status?.active || 0}</p>
            <p className="text-xs text-blue-600 uppercase font-medium">Active Delays</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-600">{report.by_cease_status?.ceased_confirmed || 0}</p>
            <p className="text-xs text-slate-500 uppercase font-medium">Ceased</p>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> By Event Family
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownBar data={report.by_family} colorMap={{}} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <Scale className="h-4 w-4" /> By Source
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownBar data={report.by_source} colorMap={sourceColors} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <Lock className="h-4 w-4" /> By Classification Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownBar data={report.by_classification_status} colorMap={classificationColors} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <Clock className="h-4 w-4" /> By Lifecycle Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownBar data={report.by_status} colorMap={statusColors} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <FileText className="h-4 w-4" /> By Responsible Party
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownBar data={report.by_responsible_party} colorMap={{}} />
          </CardContent>
        </Card>
      </div>

      {/* Filters + Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-700">
              Event Register ({filteredEvents.length})
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={filterFamily} onValueChange={setFilterFamily}>
                <SelectTrigger className="w-48 text-xs h-8" data-testid="report-filter-family">
                  <Filter className="h-3 w-3 mr-1" /><SelectValue placeholder="All Families" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Families</SelectItem>
                  {Object.keys(report.by_family).sort().map(f => (
                    <SelectItem key={f} value={f}>{f} ({report.by_family[f]})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger className="w-36 text-xs h-8" data-testid="report-filter-source">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {Object.keys(report.by_source).sort().map(s => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')} ({report.by_source[s]})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterClassification} onValueChange={setFilterClassification}>
                <SelectTrigger className="w-40 text-xs h-8" data-testid="report-filter-class">
                  <SelectValue placeholder="All Classifications" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classifications</SelectItem>
                  {Object.keys(report.by_classification_status).sort().map(c => (
                    <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')} ({report.by_classification_status[c]})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={handleExportCSV} className="text-xs h-8" data-testid="export-csv-btn">
                <Download className="h-3 w-3 mr-1" /> CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="event-register-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 font-bold uppercase text-slate-500">Event #</th>
                  <th className="text-left py-2 px-2 font-bold uppercase text-slate-500">Family</th>
                  <th className="text-left py-2 px-2 font-bold uppercase text-slate-500">Source</th>
                  <th className="text-left py-2 px-2 font-bold uppercase text-slate-500">Status</th>
                  <th className="text-left py-2 px-2 font-bold uppercase text-slate-500">Classification</th>
                  <th className="text-left py-2 px-2 font-bold uppercase text-slate-500">Clause</th>
                  <th className="text-left py-2 px-2 font-bold uppercase text-slate-500">Entitlement</th>
                  <th className="text-left py-2 px-2 font-bold uppercase text-slate-500">Party</th>
                  <th className="text-right py-2 px-2 font-bold uppercase text-slate-500">Days</th>
                  <th className="text-center py-2 px-2 font-bold uppercase text-slate-500">Notice</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map(e => (
                  <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => window.location.href = `/events/${e.id}`}>
                    <td className="py-2 px-2 font-mono font-medium text-slate-800">{e.event_number}</td>
                    <td className="py-2 px-2 text-slate-600 max-w-[160px] truncate" title={e.event_family}>{e.event_family}</td>
                    <td className="py-2 px-2">
                      <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px] uppercase">{(e.event_source || 'manual').replace(/_/g, ' ')}</Badge>
                    </td>
                    <td className="py-2 px-2">
                      <Badge className={`text-[10px] ${
                        e.status === 'pending_classification' ? 'bg-amber-100 text-amber-800' :
                        e.status === 'clause_assigned' ? 'bg-cyan-100 text-cyan-700' :
                        e.status === 'notice_created' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>{(e.status || 'draft').replace(/_/g, ' ')}</Badge>
                    </td>
                    <td className="py-2 px-2">
                      {e.classification_status === 'pending' ? (
                        <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Lock className="h-2.5 w-2.5 mr-0.5" />Pending</Badge>
                      ) : e.classification_status === 'completed' ? (
                        <Badge className="bg-green-100 text-green-700 text-[10px]"><CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />Done</Badge>
                      ) : (
                        <span className="text-slate-400">N/A</span>
                      )}
                    </td>
                    <td className="py-2 px-2 font-mono text-slate-600 max-w-[120px] truncate" title={e.clause_reference}>{e.clause_reference || '-'}</td>
                    <td className="py-2 px-2">
                      {e.entitlement_category ? (
                        <Badge className={`text-[10px] ${
                          e.entitlement_category === 'EOT + Cost' ? 'bg-purple-100 text-purple-700' :
                          e.entitlement_category === 'Cost' ? 'bg-blue-100 text-blue-700' :
                          'bg-teal-100 text-teal-700'
                        }`}>{e.entitlement_category}</Badge>
                      ) : '-'}
                    </td>
                    <td className="py-2 px-2 text-slate-600 max-w-[100px] truncate">{e.responsible_party}</td>
                    <td className="py-2 px-2 text-right font-mono text-slate-600">{e.days_ongoing}</td>
                    <td className="py-2 px-2 text-center">
                      {e.has_notice ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600 mx-auto" /> : 
                       e.notice_generation_locked ? <Lock className="h-3.5 w-3.5 text-amber-500 mx-auto" /> : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredEvents.length === 0 && (
            <p className="text-center text-slate-400 py-8 text-sm">No events match the current filters.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
